import { cloneDeep, orderBy, sortBy } from 'lodash-es';
import { EMPTY_MTICKET_INFO, EMPTY_SWIFT_CARD_INFO } from '@nx-ng/constants';
import {
  BookingSummary,
  BookingSummaryItem,
  DeliveryMethod,
  EMPTY_CUSTOMER_DETAILS,
  IBasketItemForGtmCheckout,
  ICustomerDetails,
  ITicketDescriptionEntry,
  ITicketDescriptionFields,
} from '@nx-ng/types';

import {
  EMPTY_CHILD_EDUCATION_DETAILS,
  EMPTY_STUDENT_DETAILS,
} from '@nx-ng/constants';
import { Basket, BasketItem } from '../../client/src';
import { MTICKET_FULFILLMENT, SWIFT_FULFILLMENT } from '../../constants';
import {
  hasChildTickets,
  configureCommunicationPreferences,
} from '../../util/configure-communication-preferences';

/*
Create one basket item entry per applicant
based on quantity of tickets ordered
*/
export const annotateBasketWithDescriptions = ({
  basket,
  descriptions,
  savedBookingSummary,
}: {
  basket: Basket | undefined;
  descriptions: ITicketDescriptionFields;
  savedBookingSummary: BookingSummary | undefined;
}): BookingSummaryItem[] => {
  if (!basket) {
    return [];
  }

  try {
    const items: BookingSummaryItem[] = (basket?.basketItems || [])
      .flatMap((item: BasketItem) => {
        const summaryItems: BookingSummaryItem[] = [];

        for (let quantityIndex = 1; quantityIndex <= item.quantity; quantityIndex++) {
          const summaryItem = mapBasketItemToBookingSummaryItem({
            basket,
            descriptions,
            savedBookingSummary,
            item,
            quantityIndex
          });

          if (summaryItem) {
            summaryItems.push(summaryItem)
          }
        }

        return summaryItems;
      })
      .filter(
        (item: BookingSummaryItem | undefined): boolean => item !== undefined
      );

    const directDebitTickets = orderBy(
      items.filter((item: BookingSummaryItem) => item.ticket.isDirectDebit),
      (item: BookingSummaryItem) => item.ticket.subTotalPrice,
      'asc'
    );

    return [
      ...directDebitTickets,
      ...items.filter((item: BookingSummaryItem) => !item.ticket.isDirectDebit),
    ];
  } catch (error) {
    console.log('Error building booking summary page basket items', error);
    return [];
  }
};

const mapBasketItemToBookingSummaryItem = ({
  basket,
  descriptions,
  savedBookingSummary,
  item,
  quantityIndex
}: {
  basket: Basket;
  descriptions: ITicketDescriptionFields;
  savedBookingSummary: BookingSummary | undefined;
  item: BasketItem;
  quantityIndex: BookingSummaryItem["quantityIndex"]
}): BookingSummaryItem | undefined => {
  if (!descriptions[item.ticketID]) {
    return undefined;
  }
  const existingBookingSummaryItem: BookingSummaryItem | undefined =
    savedBookingSummary?.items?.find(
      (existingItem) => existingItem.basketItemId === item.ticketID && existingItem.quantityIndex === quantityIndex
    );

  return createBookingSummaryItem({
    basketId: basket.basketId,
    basketItem: item,
    ticketId: item.ticketID,
    desc: descriptions[item.ticketID],
    existingBookingSummaryItem,
    quantityIndex,
  });
};

export const createBookingSummaryItem = ({
  basketId,
  basketItem,
  ticketId,
  desc,
  existingBookingSummaryItem,
  quantityIndex
}: {
  basketId: string;
  basketItem: BasketItem;
  ticketId: number;
  desc: ITicketDescriptionEntry;
  existingBookingSummaryItem: BookingSummaryItem | undefined;
  quantityIndex: BookingSummaryItem["quantityIndex"];
}): BookingSummaryItem => {
  const alias = desc.alias || '';

  const swiftEnabled = isSwiftEnabledForTicket(desc);
  const mTicketEnabled = isMTicketEnabledForTicket(desc);

  return {
    basketItemId: basketItem.ticketID,
    forBasket: basketId,
    quantityIndex: quantityIndex,
    customerType: Number(desc.customerTypeId),
    customerTypeName: desc.customerType,
    mTicketEnabled,
    swiftEnabled: swiftEnabled,
    selectedDeliveryMethod: determineDeliveryMethod({
      existingDeliveryMethod:
        existingBookingSummaryItem?.selectedDeliveryMethod,
      mTicketEnabled,
    }),
    ticket: {
      id: ticketId,
      alias,
      subtitle: `${alias}-subtitle`,
      title: desc.title,
      type: desc.ticketType,
      summary: desc.description,
      count: basketItem.quantity,
      subTotalPrice: basketItem.subTotalPrice, // price for all (count) items
      travelMethod: desc.travelMethod,
      zone: desc.zone,
      isDirectDebit: desc.isDirectDebit,
      termDateFrom: desc.termDateFrom || '',
      termDateTo: desc.termDateTo || '',
      minAgeBand: basketItem.minAgeBand,
      maxAgeBand: basketItem.maxAgeBand,
    },
    ticketIsForApplicant:
      existingBookingSummaryItem?.ticketIsForApplicant || undefined,
    customerDetails: determineCustomerDetails({
      existingCustomerDetails: existingBookingSummaryItem?.customerDetails,
    }),
    studentDetails: existingBookingSummaryItem
      ? cloneDeep(existingBookingSummaryItem.studentDetails)
      : cloneDeep(EMPTY_STUDENT_DETAILS),
    childEducationDetails: existingBookingSummaryItem
      ? cloneDeep(existingBookingSummaryItem.childEducationDetails)
      : cloneDeep(EMPTY_CHILD_EDUCATION_DETAILS),
    mTicketDetails: existingBookingSummaryItem
      ? cloneDeep(existingBookingSummaryItem.mTicketDetails)
      : cloneDeep(EMPTY_MTICKET_INFO),
    swiftCardDetails: existingBookingSummaryItem
      ? cloneDeep(existingBookingSummaryItem.swiftCardDetails)
      : cloneDeep(EMPTY_SWIFT_CARD_INFO),
  };
};

export const determineCustomerDetails = ({
  existingCustomerDetails,
}: {
  existingCustomerDetails: ICustomerDetails | undefined;
}) => {
  if (existingCustomerDetails) {
    return cloneDeep(existingCustomerDetails);
  }
  return cloneDeep(EMPTY_CUSTOMER_DETAILS);
};

export const determineDeliveryMethod = ({
  mTicketEnabled,
  existingDeliveryMethod,
}: {
  mTicketEnabled: boolean;
  existingDeliveryMethod: DeliveryMethod | undefined;
}) => {
  if (existingDeliveryMethod) {
    return existingDeliveryMethod;
  }
  return mTicketEnabled ? DeliveryMethod.MTicket : DeliveryMethod.Swift;
};

export const isMTicketEnabledForTicket = (
  ticket: ITicketDescriptionEntry
): boolean => {
  const firstPrice = ticket.prices[0];
  if (!firstPrice) {
    return false;
  }
  return firstPrice.fulfillmentMethod?.includes(MTICKET_FULFILLMENT) || false;
};

export const isSwiftEnabledForTicket = (
  ticket: ITicketDescriptionEntry
): boolean => {
  const firstPrice = ticket.prices[0];
  if (!firstPrice) {
    return false;
  }
  return firstPrice.fulfillmentMethod?.includes(SWIFT_FULFILLMENT) || false;
};

export const buildBasketItemsForGtmCheckout = ({
  basket,
  bookingItems,
}: {
  basket?: Basket;
  bookingItems: BookingSummaryItem[];
}): IBasketItemForGtmCheckout[] => {
  if (!basket) {
    return [];
  }

  return (
    basket.basketItems?.map((item: BasketItem) => {
      const bookingItem = bookingItems.find(
        (bookingItem: BookingSummaryItem) =>
          bookingItem.basketItemId === item.ticketID
      );

      return {
        ...item,
        deliveryMethod: bookingItem?.selectedDeliveryMethod,
        isDirectDebit: bookingItem?.ticket.isDirectDebit || false,
      };
    }) || []
  );
};

export const buildBookingSummaryPageDetails = ({
  bookingItems,
  customerDetails,
  savedBookingSummary,
}: {
  bookingItems: BookingSummaryItem[];
  customerDetails: ICustomerDetails | undefined;
  savedBookingSummary: BookingSummary | undefined;
}): BookingSummary => {
  const accountHolderCustomerDetails = customerDetails
    ? cloneDeep(customerDetails)
    : cloneDeep(EMPTY_CUSTOMER_DETAILS);
  return {
    items: cloneDeep(bookingItems),
    accountHolder: customerDetails,
    billingAddress: savedBookingSummary
      ? cloneDeep(savedBookingSummary.billingAddress)
      : accountHolderCustomerDetails,
    communicationPreferences: savedBookingSummary
      ? {
          ...cloneDeep(savedBookingSummary.communicationPreferences),
          showGuardianConsent: hasChildTickets(bookingItems),
        }
      : configureCommunicationPreferences(bookingItems),
  };
};
